import type { CSSProperties, MouseEvent } from 'react';
import { docIconId } from '../lib/docs';
import { ROW_H } from '../lib/layout/constants';
import type { DocId, DocType } from '../lib/types';

/**
 * One documentation pill, in every place documentation is offered.
 *
 * WHY THIS IS ITS OWN COMPONENT, and the first thing built in the doc-overlay step rather
 * than part of it: the vanilla builder `fillDocPill()` had seven call sites across THREE
 * surfaces — the doc beans under a map node (`nodeEl`), the pills in a node's popup
 * (`openPopup`), and the pills in the doc overlay's navigation band (`docNavPill`). Two of
 * those three belonged to the map, which ported later. Porting the overlay with its own private
 * pill builder would have left two live implementations of the one thing `infostation.html`'s
 * own stylesheet calls "one documentation-pill format" — precisely the failure mode this
 * migration is most likely to produce. So the pill moved ONCE, ahead of its first consumer,
 * and each surface adopted it as it arrived: the band, then the map's rows, then the popup's
 * blocks. **`fillDocPill()` now has zero callers and is deleted**, along with `docIconEl`,
 * `setDocPillLabel` and `svgIcon`. This is the only pill implementation in the app.
 *
 * ROW_H IS THE REASON THE COUPLING MATTERS. `.beanrow`'s height and `docExtraHeight()`'s
 * `ROW_H` reserve the same space for the same row; if they disagree the expanded map layout
 * desynchronises silently — nodes overlap and wires miss their anchors, with nothing to
 * error. CLAUDE.md names the hazard and the plan names the fix: one exported constant. The
 * `row` variant below sets its height FROM `lib/layout/constants.ts`, so the React pill and
 * the layout maths cannot drift even before the vanilla stylesheet goes. (The stylesheet's
 * own `height:22px` still governs the vanilla pills until the map ports; `docHeight.test.ts`
 * reads the CSS and fails on drift, which is the enforceable half of the same idea.)
 *
 * MARKUP-PRESERVING. This renders exactly what `fillDocPill()` built — the same element
 * kinds, the same classes, the same inline `background` / `border-color`, the same
 * `title`-defaults-to-label — because every pixel baseline in `e2e/__screenshots__` has to
 * pass unedited. The variants below are the four sizes the stylesheet already defines, not
 * new ones.
 *
 * THE COLOUR IS INLINE, NOT A CLASS, and that is deliberate in the original: a pill's colour
 * comes from the SLOT it occupies (`DOC_BEAN_COLOR`), and custom docs have no slot, so it is
 * a value rather than one of a closed set of classes. The type ICON needs no colour of its
 * own — `.ico` uses `stroke: currentColor` and inherits the pill's white text.
 */

/** The four sizes the stylesheet defines. Each maps to one modifier class. */
export type DocPillVariant =
  /** `.docnav-pill` — the doc overlay's navigation band. */
  | 'nav'
  /** `.docpill-block` — a full-width button in a node's popup. */
  | 'block'
  /** `.beanrow` — one documentation row under an expanded map node. */
  | 'row'
  /** `.docpill-lg` — a page-sized button that leaves the app. */
  | 'lg';

const VARIANT_CLASS: Record<DocPillVariant, string> = {
  nav: 'docnav-pill',
  block: 'docpill-block',
  row: 'beanrow',
  lg: 'docpill-lg',
};

/**
 * Icon size per variant, matching what each `fillDocPill()` call site passed.
 *
 * A pill's icon is sized to its row, not to a global default: 11px fits inside the 22px
 * beanrow, 17px suits a page-sized button. Keeping the numbers here rather than at the call
 * sites is what stops one surface quietly drifting a pixel from another.
 */
const VARIANT_ICON_PX: Record<DocPillVariant, number> = { nav: 13, block: 16, row: 11, lg: 17 };

/** `<button>` where the original built one; `<span>` where it built a span. */
const VARIANT_TAG: Record<DocPillVariant, 'button' | 'span'> = {
  nav: 'span',
  block: 'button',
  row: 'span',
  lg: 'button',
};

export interface DocPillProps {
  variant: DocPillVariant;
  /** What the pill says, and — unless `title` overrides it — what its tooltip says. */
  label: string;
  /**
   * The pill's fill. A CSS colour or custom property (`var(--doc-color)`); the border is
   * derived from it the way the original did, with `color-mix`.
   */
  color?: string;
  /** The page this pill opens. Only used to look its type icon up. */
  docId?: DocId | null;
  /** `docTypes` from the map payload — absent means the page is `standard`. */
  docTypes?: Record<DocId, DocType>;
  /**
   * An explicit icon symbol id, overriding the type lookup — `null` draws no icon at all.
   *
   * Both cases are real: the "open in a new tab" pill states its icon (it is about leaving
   * the app, not about what the page holds), and the "+N more" stand-in has no page and so
   * no type to draw.
   */
  icon?: string | null;
  iconPx?: number;
  /** Tooltip. Defaults to the label, as `fillDocPill()` did. */
  title?: string;
  /**
   * Navigation band: this pill's page is the one on screen. Inverted — a light wash of the
   * kind colour, with the colour itself kept as the border and the text.
   */
  active?: boolean;
  /**
   * Navigation band, edit mode: the slot is EMPTY and this pill creates the page.
   *
   * A dashed outline with no icon and no fill, because there is no document yet to have a
   * type or a colour. `.docnav-pill.create` carries the whole look, so no inline colour is
   * written — writing one would fight the `!important` in the stylesheet.
   */
  create?: boolean;
  /**
   * A PLANNED-but-missing pill (V0.42): the plan names this document and it is not attached.
   * Distinct from `create` — that one is the nav band's neutral "add a page to an empty slot"
   * affordance; `planned` is coloured in the KIND'S colour, dashed and unfilled, and appears
   * on all three surfaces. No icon (there is no page yet to have a type).
   */
  planned?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /**
   * `data-*` attributes to put on the pill itself.
   *
   * The map's rows carry the delegation markers its pointer handler reads
   * (`data-opendoc-kind` and friends), and `closest()` has to find them on the pill — a
   * wrapper element would work for the lookup and break `.docbeans.expanded > .beanrow`,
   * which sizes the row as a direct child.
   */
  data?: Record<string, string>;
}

export function DocPill({
  variant,
  label,
  color,
  docId,
  docTypes,
  icon,
  iconPx,
  title,
  active,
  create,
  planned,
  disabled,
  className,
  style,
  onClick,
  data,
}: DocPillProps) {
  const classes = ['docpill', VARIANT_CLASS[variant]];
  if (create) classes.push('create');
  if (planned) classes.push('planned');
  if (active) classes.push('active');
  if (className) classes.push(className);

  // `icon` wins over the type lookup; `icon === null` says "no icon"; a create or planned pill
  // never has one, because there is no page yet to have a type.
  const symbol =
    create || planned || icon === null ? null : (icon ?? (docTypes ? docIconId(docTypes, docId) : null));
  const px = iconPx ?? VARIANT_ICON_PX[variant];

  const colours: CSSProperties = create
    ? {}
    : planned
      ? // Dashed outline in the kind's colour, no fill; `.docpill.planned` carries the dashed
        // border-style, transparent background and weight so nothing here fights it.
        { borderColor: color, color }
      : active
        ? {
            background: `color-mix(in srgb,${color} 15%, transparent)`,
            borderColor: color,
            color,
          }
        : {
            background: color,
            borderColor: `color-mix(in srgb,${color} 70%, black)`,
          };

  const common = {
    className: classes.join(' '),
    title: title ?? label,
    style: {
      // The one row whose height is a layout input rather than a look. See the note above.
      ...(variant === 'row' ? { height: ROW_H } : null),
      ...colours,
      ...style,
    } satisfies CSSProperties,
    onClick,
    ...data,
  };

  const body = (
    <>
      {symbol ? (
        <svg className="ico" style={{ width: px, height: px }}>
          <use href={`#${symbol}`} />
        </svg>
      ) : null}
      {/* A create pill is bare text, as the original was: it has no label to ellipsise
          against a fill, and `.lbl`'s overflow rules would only shorten the word "Create". */}
      {create ? label : <span className="lbl">{label}</span>}
    </>
  );

  // Two branches rather than a dynamic tag: only the button form can be disabled, and the
  // original built exactly these two element kinds per surface.
  return VARIANT_TAG[variant] === 'button' ? (
    <button type="button" {...common} disabled={disabled}>
      {body}
    </button>
  ) : (
    <span {...common}>{body}</span>
  );
}
